import {PropsWithChildren, useState} from 'react'
import {useLocation, Navigate} from 'react-router'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {equalExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {useDistricts} from '@freckle/educator-entities/ts/common/hooks/use-districts'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import {DistrictAttrs} from '@freckle/educator-entities/ts/roster/models/district'
import {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {WithResources} from '@freckle/classroom/ts/common/components/with-resources'
import {WithAuthStatus} from '@freckle/classroom/ts/common/routers/router-v2/with-auth-status'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {
  useHasStarPlacements,
  placementsMayExist,
} from '@freckle/classroom/ts/hooks/placement-summaries'
import {usePremiumLicenses} from '@freckle/classroom/ts/hooks/use-premium-licenses'
import {useStudents} from '@freckle/classroom/ts/hooks/use-students'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import {initializePendo} from './../app-router'
import {RedirectSet} from './cookie-redirect'

type WrapperProps = PropsWithChildren<{}>

type DataProps = {
  teacher: TeacherAttrs
  premiumLicenses: PremiumLicensesAttrs
  students: Array<StudentAttrs>
  schools: SchoolAttrs[]
  districts: DistrictAttrs[]
}

type Props = WrapperProps & DataProps

function PendoInner(props: Props) {
  const {teacher, premiumLicenses, students, schools, districts} = props
  const location = useLocation()
  const [state, setState] = useState<'loading' | 'pendo-initialized' | 'error'>('loading')
  const hasStarMathPlacements = useHasStarPlacements('math', {
    enabled: placementsMayExist('math', schools, premiumLicenses),
  })
  const hasStarReadingPlacements = useHasStarPlacements('ela', {
    enabled: placementsMayExist('ela', schools, premiumLicenses),
  })

  useSafeEffectExtraDeps<DataProps>(
    ({teacher, schools, districts, premiumLicenses, students}) => {
      async function setupPendoClient() {
        try {
          initializePendo({
            teacher,
            districts,
            schools,
            premiumLicenses,
            students,
            hasStarMathPlacements,
            hasStarReadingPlacements,
          })

          setState('pendo-initialized')
        } catch {
          setState('error')
        }
      }

      setupPendoClient()
    },
    [hasStarMathPlacements, hasStarReadingPlacements],
    {
      teacher: equalExtra(teacher),
      schools: equalExtra(schools),
      districts: equalExtra(districts),
      premiumLicenses: equalExtra(premiumLicenses),
      students: equalExtra(students),
    }
  )

  return (
    <>
      {state === 'error' && (
        <Navigate
          to={location.search ? `${Routes.login()}${location.search}` : Routes.login()}
          replace
        />
      )}
      {state === 'pendo-initialized' && props.children}
    </>
  )
}

export const PendoWrapper = (props: PropsWithChildren<{}>) => {
  const location = useLocation()
  return (
    <WithAuthStatus
      render={({loggedIn}) =>
        loggedIn ? (
          <Pendo {...props} />
        ) : (
          <RedirectSet
            to={location.search ? `${Routes.login()}${location.search}` : Routes.login()}
          />
        )
      }
    />
  )
}

function Pendo(props: PropsWithChildren<{}>) {
  const premiumLicenses = usePremiumLicenses()
  const teacher = useTeacher()
  const students = useStudents()
  const schools = useSchools()
  const districts = useDistricts()
  return (
    <WithResources
      resources={{teacher, premiumLicenses, students, schools, districts}}
      render={({teacher, premiumLicenses, students, schools, districts}) => (
        <PendoInner
          teacher={teacher}
          premiumLicenses={premiumLicenses}
          students={students}
          schools={schools}
          districts={districts}
          {...props}
        />
      )}
      noSpinner
    />
  )
}
